import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { membershipApplicationAPI, membersAPI } from '../../services/api';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import { ArrowLeft, Save, UserPlus, Upload, X, Plus } from 'lucide-react';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';

const calculateAge = (dob) => {
  if (!dob) return '';
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return String(age);
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const FormCard = styled(Card)`
  padding: 2rem;
`;

const FormField = styled.div`
  margin-bottom: 1.5rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
  
  .required {
    color: ${props => props.theme.colors.error};
  }
`;

const FieldHelp = styled.p`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0.25rem 0;
`;

const FieldError = styled.p`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.error};
  margin: 0.25rem 0;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const RadioOption = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme, $selected }) =>
    $selected ? theme.colors.primary : theme.colors.border.light};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary + '10' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary + '10'};
  }
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-top: 0.5rem;
`;

const TrainingEntry = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1rem;
  margin-bottom: 1rem;
  position: relative;
  background: ${props => props.theme.colors.background.secondary};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const DISABILITY_OPTIONS = [
  'Physical',
  'Visual',
  'Hearing',
  'Intellectual',
  'Psychosocial',
  'Other (please specify)'
];

const OTHER_DISABILITY_OPTION = 'Other (please specify)';

const TRAINING_OPTIONS = [
  'Human Rights Basics',
  'Gender Equality',
  'Advocacy Skills',
  'Community Organizing',
  'Legal Aid',
  'Research Methods',
  'Leadership Development',
  'Project Management',
  'Digital Security',
  'Media & Communication'
];

const AdminMemberForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [member, setMember] = useState(null);
  const [removeCv, setRemoveCv] = useState(false);
  
  const idCardRef = useRef(null);
  const cvInputRef = useRef(null);
  const certificateRefs = useRef({});

  // Disability state
  const disabilityStatus = formData.has_disability || 'no';
  const disabilitySelections = Array.isArray(formData.disability_types) ? formData.disability_types : [];
  const disabilityOther = formData.disability_other || '';

  // Training state
  const hasPreviousTraining = formData.has_previous_training === 'yes';
  const previousTrainings = Array.isArray(formData.previous_trainings) ? formData.previous_trainings : [];
  const interestedTrainings = Array.isArray(formData.interested_trainings) ? formData.interested_trainings : [];

  // Fetch form configuration and member data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      try {
        // Get public forms and find the active one
        const formResponse = await membershipApplicationAPI.getPublicForms();
        const forms = formResponse.data?.data || formResponse.data || [];
        const activeForm = Array.isArray(forms) ? forms.find(f => f.is_active) || forms[0] : forms;
        
        if (activeForm) {
          setForm(activeForm);
        }
        
        // Initialize form data with default values
        const activeFields = activeForm?.activeFields || activeForm?.active_fields || [];
        const initialData = {};
        
        // Initialize all fields from form config
        activeFields.forEach(field => {
          if (field.field_type === 'checkbox') {
            initialData[field.field_name] = [];
          } else if (field.field_type === 'file') {
            initialData[field.field_name] = null;
          } else {
            initialData[field.field_name] = '';
          }
        });
        
        // Add special fields
        initialData.has_disability = 'no';
        initialData.disability_types = [];
        initialData.disability_other = '';
        initialData.cv = null;
        initialData.id_card = null;
        initialData.interested_trainings = [];
        initialData.has_previous_training = 'no';
        initialData.previous_trainings = [];
        
        // If edit mode, fetch member data and populate form
        if (isEditMode && id) {
          try {
            const response = await membersAPI.getById(id);
            const memberData = response.data?.data || response.data;
            setMember(memberData);
            
            console.log('Fetched member data:', memberData);
            
            // Start with initial data
            const memberFormData = { ...initialData };
            
            // Map all form builder fields from member data
            // First, map common field names
            const fieldMappings = {
              'first_name': memberData.first_name,
              'middle_name': memberData.middle_name,
              'last_name': memberData.last_name,
              'full_name': `${memberData.first_name || ''} ${memberData.middle_name || ''} ${memberData.last_name || ''}`.trim(),
              'email': memberData.email,
              'phone': memberData.phone || memberData.phone_number,
              'phone_number': memberData.phone || memberData.phone_number,
              'gender': memberData.gender,
              'age': memberData.date_of_birth ? calculateAge(memberData.date_of_birth) : '',
              'date_of_birth': memberData.date_of_birth,
              'region': memberData.region?.name || memberData.city,
              'region_city': memberData.city || memberData.region?.name || '',
              'sector': memberData.organization_type,
              'organization_type': memberData.organization_type,
              'occupation': memberData.occupation,
              'occupation_position': memberData.occupation || memberData.position_title,
              'position_title': memberData.position_title,
              'organization_name': memberData.organization_name,
              'thematic_areas': Array.isArray(memberData.thematic_areas) 
                ? memberData.thematic_areas.join(', ') 
                : (memberData.thematic_areas || ''),
              'short_biography': memberData.bio,
              'bio': memberData.bio,
              'interest_in_ewhrdn': memberData.interest_in_ewhrdn || '',
              'womens_rights_issues': memberData.womens_rights_issues || '',
              'challenges_gender_equality': memberData.challenges_gender_equality || '',
              'political_affiliation_declaration': memberData.political_affiliation_declaration || false,
              'signature_full_name': memberData.signature_full_name || '',
              'email_confirmation': memberData.email_confirmation || '',
            };
            
            // Apply field mappings
            Object.entries(fieldMappings).forEach(([key, value]) => {
              // Always set the value, even if empty, to ensure form fields are initialized
              if (value !== undefined && value !== null) {
                memberFormData[key] = value;
              }
            });
            
            // Ensure gender is set (handle case variations)
            // First, ensure gender is set from fieldMappings (which was already applied above)
            // Then try to normalize it to match form options
            
            // Get gender field options from form builder if available
            const genderField = activeFields.find(f => f.field_name === 'gender');
            const genderOptions = genderField?.options || ['Male', 'Female', 'Other', 'male', 'female', 'other'];
            
            // Check multiple sources for gender
            let genderValue = memberFormData.gender || 
                             memberData.gender || 
                             '';
            
            if (genderValue && genderValue !== '') {
              const genderLower = String(genderValue).toLowerCase().trim();
              
              // Try to match with form options (case-insensitive)
              const matchingOption = genderOptions.find(opt => 
                String(opt).toLowerCase() === genderLower
              );
              
              if (matchingOption) {
                // Use the exact option value from form builder
                memberFormData.gender = matchingOption;
              } else {
                // Fallback: try common variations
                if (genderLower === 'male' || genderLower === 'm') {
                  const maleOption = genderOptions.find(o => o.toLowerCase() === 'male');
                  memberFormData.gender = maleOption || genderOptions[0] || genderValue;
                } else if (genderLower === 'female' || genderLower === 'f') {
                  const femaleOption = genderOptions.find(o => o.toLowerCase() === 'female');
                  memberFormData.gender = femaleOption || genderOptions[1] || genderValue;
                } else if (genderLower === 'other' || genderLower === 'o') {
                  const otherOption = genderOptions.find(o => o.toLowerCase() === 'other');
                  memberFormData.gender = otherOption || genderOptions[2] || genderValue;
                } else {
                  // Keep original value - renderField will handle normalization in the UI
                  memberFormData.gender = genderValue;
                }
              }
            } else {
              // If no gender found, ensure it's set as empty string
              memberFormData.gender = '';
            }
            
            console.log('Gender mapping:', {
              memberData_gender: memberData.gender,
              genderValue: genderValue,
              mapped_gender: memberFormData.gender,
              genderOptions: genderOptions,
              final_gender: memberFormData.gender
            });
            
            // Ensure sector is set (try multiple field names)
            if (!memberFormData.sector || memberFormData.sector === '') {
              memberFormData.sector = memberData.organization_type || 
                                      memberData.sector || 
                                      memberData.sector_other || 
                                      '';
            }
            
            console.log('Field mappings applied:', {
              gender: memberFormData.gender,
              sector: memberFormData.sector,
              organization_type: memberData.organization_type
            });
            
            // Map special fields - training data
            // Check multiple possible sources for interested_trainings
            let interestedTrainings = [];
            if (Array.isArray(memberData.interested_trainings) && memberData.interested_trainings.length > 0) {
              interestedTrainings = memberData.interested_trainings;
            } else if (typeof memberData.interested_trainings === 'string') {
              try {
                interestedTrainings = JSON.parse(memberData.interested_trainings);
              } catch {
                interestedTrainings = memberData.interested_trainings.split(',').map(s => s.trim()).filter(Boolean);
              }
            }
            memberFormData.interested_trainings = interestedTrainings;
            
            // Check has_previous_training
            if (memberData.has_previous_training !== undefined && memberData.has_previous_training !== null) {
              memberFormData.has_previous_training = memberData.has_previous_training ? 'yes' : 'no';
            } else {
              memberFormData.has_previous_training = 'no';
            }
            
            // Map previous trainings data
            let previousTrainings = [];
            
            // Try multiple sources for previous trainings
            let trainingData = null;
            
            // Check memberData.previous_trainings_data first
            if (memberData.previous_trainings_data) {
              trainingData = memberData.previous_trainings_data;
            } 
            // Also check memberData.previous_trainings (alternative field name)
            else if (memberData.previous_trainings) {
              trainingData = memberData.previous_trainings;
            }
            
            if (trainingData) {
              if (Array.isArray(trainingData) && trainingData.length > 0) {
                previousTrainings = trainingData.map(t => {
                  // Handle different data structures
                  const training = {
                    name: t.training_name || t.name || '',
                    institution: t.institution || t.trainer || '',
                    date: t.training_date || t.date || '',
                    certificate: null
                  };
                  return training;
                });
              } else if (typeof trainingData === 'string' && trainingData.trim() !== '') {
                try {
                  const parsed = JSON.parse(trainingData);
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    previousTrainings = parsed.map(t => ({
                      name: t.training_name || t.name || '',
                      institution: t.institution || t.trainer || '',
                      date: t.training_date || t.date || '',
                      certificate: null
                    }));
                  }
                } catch (e) {
                  console.log('Could not parse previous_trainings_data:', e, trainingData);
                }
              }
            }
            
            // If no previous trainings but has_previous_training is yes, initialize with one empty entry
            if (memberFormData.has_previous_training === 'yes' && previousTrainings.length === 0) {
              previousTrainings = [{ name: '', institution: '', date: '', certificate: null }];
            }
            
            memberFormData.previous_trainings = previousTrainings;
            
            console.log('Training data mapped:', {
              has_previous_training: memberFormData.has_previous_training,
              previous_trainings_data: memberData.previous_trainings_data,
              previous_trainings: memberData.previous_trainings,
              mapped_previous_trainings: memberFormData.previous_trainings,
              interested_trainings: memberFormData.interested_trainings
            });
            memberFormData.has_disability = memberData.has_disability ? 'yes' : 'no';
            memberFormData.disability_types = Array.isArray(memberData.disability_types) ? memberData.disability_types : [];
            memberFormData.disability_other = memberData.disability_other || '';
            
            // Map all other form builder fields from member data
            // Check if member has additional fields stored
            // Fields that should not be overwritten if already set
            const protectedFields = ['gender', 'sector', 'organization_type', 'has_previous_training', 'previous_trainings', 'interested_trainings'];
            
            activeFields.forEach(field => {
              const fieldName = field.field_name;
              // If field not already mapped, try to get from memberData
              // Don't overwrite protected fields if they're already set
              const isProtected = protectedFields.includes(fieldName);
              const shouldMap = !memberFormData.hasOwnProperty(fieldName) || 
                               memberFormData[fieldName] === '' || 
                               (!isProtected && memberFormData[fieldName] === '');
              
              if (shouldMap) {
                // Try direct match first
                if (memberData[fieldName] !== undefined && memberData[fieldName] !== null) {
                  if (field.field_type === 'checkbox' && !Array.isArray(memberData[fieldName])) {
                    memberFormData[fieldName] = typeof memberData[fieldName] === 'string' 
                      ? memberData[fieldName].split(',').map(s => s.trim()).filter(Boolean)
                      : [];
                  } else if (fieldName === 'gender') {
                    // Special handling for gender to normalize it
                    const genderLower = String(memberData[fieldName]).toLowerCase().trim();
                    if (genderLower === 'male' || genderLower === 'm') {
                      memberFormData[fieldName] = 'male';
                    } else if (genderLower === 'female' || genderLower === 'f') {
                      memberFormData[fieldName] = 'female';
                    } else if (genderLower === 'other' || genderLower === 'o') {
                      memberFormData[fieldName] = 'other';
                    } else {
                      memberFormData[fieldName] = memberData[fieldName];
                    }
                  } else {
                    memberFormData[fieldName] = memberData[fieldName];
                  }
                }
                // Try snake_case version
                else if (memberData[fieldName.replace(/_/g, '_')] !== undefined) {
                  memberFormData[fieldName] = memberData[fieldName.replace(/_/g, '_')];
                }
              }
            });
            
            // Try to fetch submission data if available (for original form data)
            try {
              const submissionResponse = await membershipApplicationAPI.getSubmissions({ 
                registration_no: memberData.registration_no 
              });
              const submissions = submissionResponse.data?.data || submissionResponse.data || [];
              if (Array.isArray(submissions) && submissions.length > 0) {
                const submission = submissions[0];
                console.log('Fetched submission data:', submission);
                
                // Update gender from submission ONLY if member data doesn't have it
                // Prioritize member data over submission data
                if (submission.form_data?.gender) {
                  const currentGender = memberFormData.gender || memberData.gender || '';
                  
                  // Only use submission gender if current gender is empty
                  if (!currentGender || currentGender === '') {
                    const genderField = activeFields.find(f => f.field_name === 'gender');
                    const genderOptions = genderField?.options || ['Male', 'Female', 'Other'];
                    const genderLower = String(submission.form_data.gender).toLowerCase().trim();
                    
                    // Try to match with form options (case-insensitive)
                    const matchingOption = genderOptions.find(opt => 
                      String(opt).toLowerCase() === genderLower
                    );
                    
                    if (matchingOption) {
                      memberFormData.gender = matchingOption;
                    } else {
                      // Fallback normalization
                      if (genderLower === 'male' || genderLower === 'm') {
                        memberFormData.gender = genderOptions.find(o => o.toLowerCase() === 'male') || 'male';
                      } else if (genderLower === 'female' || genderLower === 'f') {
                        memberFormData.gender = genderOptions.find(o => o.toLowerCase() === 'female') || 'female';
                      } else if (genderLower === 'other' || genderLower === 'o') {
                        memberFormData.gender = genderOptions.find(o => o.toLowerCase() === 'other') || 'other';
                      } else {
                        memberFormData.gender = submission.form_data.gender;
                      }
                    }
                    
                    console.log('Gender updated from submission (was empty):', {
                      submission_gender: submission.form_data.gender,
                      normalized_gender: memberFormData.gender
                    });
                  } else {
                    console.log('Gender NOT updated from submission (already set from member):', {
                      current_gender: currentGender,
                      submission_gender: submission.form_data.gender
                    });
                  }
                }
                
                // Update sector from submission if not set or empty
                if ((!memberFormData.sector || memberFormData.sector === '') && submission.form_data) {
                  memberFormData.sector = submission.form_data.sector || 
                                          submission.form_data.organization_type || 
                                          memberFormData.sector;
                }
                
                // First, check for training fields directly on submission
                if (submission.interested_trainings) {
                  let subInterestedTrainings = [];
                  if (Array.isArray(submission.interested_trainings)) {
                    subInterestedTrainings = submission.interested_trainings;
                  } else if (typeof submission.interested_trainings === 'string') {
                    try {
                      subInterestedTrainings = JSON.parse(submission.interested_trainings);
                    } catch {
                      subInterestedTrainings = submission.interested_trainings.split(',').map(s => s.trim()).filter(Boolean);
                    }
                  }
                  if (subInterestedTrainings.length > 0) {
                    memberFormData.interested_trainings = subInterestedTrainings;
                  }
                }
                
                if (submission.has_previous_training !== undefined && submission.has_previous_training !== null) {
                  memberFormData.has_previous_training = submission.has_previous_training ? 'yes' : 'no';
                }
                
                // Check both previous_trainings_data and previous_trainings fields
                let subTrainingData = submission.previous_trainings_data || submission.previous_trainings;
                
                if (subTrainingData) {
                  let subPreviousTrainings = [];
                  if (Array.isArray(subTrainingData) && subTrainingData.length > 0) {
                    subPreviousTrainings = subTrainingData;
                  } else if (typeof subTrainingData === 'string' && subTrainingData.trim() !== '') {
                    try {
                      subPreviousTrainings = JSON.parse(subTrainingData);
                    } catch (e) {
                      console.log('Could not parse submission previous_trainings_data:', e);
                    }
                  }
                  if (subPreviousTrainings.length > 0) {
                    memberFormData.previous_trainings = subPreviousTrainings.map(t => ({
                      name: t.training_name || t.name || '',
                      institution: t.institution || t.trainer || '',
                      date: t.training_date || t.date || '',
                      certificate: null
                    }));
                    // Also update has_previous_training if we have training data
                    if (memberFormData.has_previous_training === 'no') {
                      memberFormData.has_previous_training = 'yes';
                    }
                  }
                }
                
                // Then merge form_data if available
                if (submission.form_data && typeof submission.form_data === 'object') {
                  // Merge submission form_data into memberFormData
                  Object.entries(submission.form_data).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && value !== '') {
                      // Handle gender specially - only use submission if member doesn't have it
                      if (key === 'gender') {
                        // Only override if member gender is empty
                        if (!memberFormData.gender || memberFormData.gender === '') {
                          const genderField = activeFields.find(f => f.field_name === 'gender');
                          const genderOptions = genderField?.options || ['Male', 'Female', 'Other'];
                          const genderLower = String(value).toLowerCase().trim();
                          const matchingOption = genderOptions.find(opt => 
                            String(opt).toLowerCase() === genderLower
                          );
                          memberFormData[key] = matchingOption || value;
                        }
                        // Skip to next iteration - don't process gender further
                        return;
                      }
                      
                      // Always use submission data for these critical fields if available (but not gender)
                      const alwaysOverrideFields = ['sector', 'organization_type', 'has_previous_training'];
                      const shouldOverride = alwaysOverrideFields.includes(key) || 
                                          !memberFormData[key] || 
                                          memberFormData[key] === '' || 
                                          (Array.isArray(memberFormData[key]) && memberFormData[key].length === 0);
                      
                      if (shouldOverride) {
                        // Handle training fields specially
                        if (key === 'interested_trainings') {
                          if (Array.isArray(value)) {
                            memberFormData[key] = value;
                          } else if (typeof value === 'string') {
                            try {
                              memberFormData[key] = JSON.parse(value);
                            } catch {
                              memberFormData[key] = value.split(',').map(s => s.trim()).filter(Boolean);
                            }
                          }
                        } else if (key === 'previous_trainings' || key === 'previous_trainings_data') {
                          if (Array.isArray(value)) {
                            memberFormData.previous_trainings = value.map(t => ({
                              name: t.training_name || t.name || '',
                              institution: t.institution || t.trainer || '',
                              date: t.training_date || t.date || '',
                              certificate: null
                            }));
                            if (memberFormData.previous_trainings.length > 0 && memberFormData.has_previous_training === 'no') {
                              memberFormData.has_previous_training = 'yes';
                            }
                          }
                        } else if (key === 'sector' || key === 'organization_type') {
                          // Set both sector and organization_type
                          memberFormData.sector = value;
                          memberFormData.organization_type = value;
                        } else {
                          memberFormData[key] = value;
                        }
                      }
                    }
                  });
                }
                
                console.log('After submission merge:', {
                  gender: memberFormData.gender,
                  sector: memberFormData.sector,
                  has_previous_training: memberFormData.has_previous_training,
                  previous_trainings: memberFormData.previous_trainings
                });
              }
            } catch (submissionError) {
              console.log('Could not fetch submission data:', submissionError);
              // Not critical, continue with member data
            }
            
            // Final check: ensure gender is set correctly
            if (!memberFormData.gender || memberFormData.gender === '') {
              // Try one more time from memberData
              if (memberData.gender) {
                const genderLower = String(memberData.gender).toLowerCase().trim();
                if (genderLower === 'male' || genderLower === 'm') {
                  memberFormData.gender = 'male';
                } else if (genderLower === 'female' || genderLower === 'f') {
                  memberFormData.gender = 'female';
                } else if (genderLower === 'other' || genderLower === 'o') {
                  memberFormData.gender = 'other';
                } else {
                  memberFormData.gender = memberData.gender;
                }
              }
            }
            
            console.log('Final form data before setting:', {
              gender: memberFormData.gender,
              memberData_gender: memberData.gender,
              sector: memberFormData.sector,
              has_previous_training: memberFormData.has_previous_training,
              previous_trainings: memberFormData.previous_trainings,
              interested_trainings: memberFormData.interested_trainings,
              memberData_keys: Object.keys(memberData)
            });
            setFormData(memberFormData);
          } catch (error) {
            console.error('Failed to fetch member', error);
            toast.error('Failed to load member data');
            setFormData(initialData);
          }
        } else {
          setFormData(initialData);
        }
      } catch (error) {
        console.error('Failed to fetch form config', error);
        toast.error('Failed to load form configuration');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [isEditMode, id]);

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setFieldErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleFileChange = (fieldName, file) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: file
    }));
    setFieldErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleDisabilityStatusChange = (value) => {
    setFormData(prev => ({
      ...prev,
      has_disability: value,
      disability_types: value === 'yes' ? (prev.disability_types || []) : [],
      disability_other: value === 'yes' ? prev.disability_other || '' : '',
    }));
  };

  const toggleDisabilityType = (option) => {
    const currentSelections = Array.isArray(disabilitySelections) ? disabilitySelections : [];
    const exists = currentSelections.includes(option);
    const nextTypes = exists
      ? currentSelections.filter(item => item !== option)
      : [...currentSelections, option];

    setFormData(prev => ({
      ...prev,
      disability_types: nextTypes,
      disability_other: nextTypes.includes(OTHER_DISABILITY_OPTION)
        ? prev.disability_other || ''
        : '',
    }));
  };

  const handleDisabilityOtherChange = (value) => {
    setFormData(prev => ({
      ...prev,
      disability_other: value,
    }));
  };

  const toggleInterestedTraining = (option) => {
    const current = Array.isArray(formData.interested_trainings) ? formData.interested_trainings : [];
    const exists = current.includes(option);
    const next = exists ? current.filter(t => t !== option) : [...current, option];
    setFormData(prev => ({ ...prev, interested_trainings: next }));
  };

  const addPreviousTraining = () => {
    setFormData(prev => ({
      ...prev,
      previous_trainings: [
        ...(prev.previous_trainings || []),
        { name: '', institution: '', date: '', certificate: null }
      ]
    }));
  };

  const removePreviousTraining = (index) => {
    setFormData(prev => ({
      ...prev,
      previous_trainings: prev.previous_trainings.filter((_, i) => i !== index)
    }));
  };

  const updatePreviousTraining = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      previous_trainings: prev.previous_trainings.map((training, i) => 
        i === index ? { ...training, [field]: value } : training
      )
    }));
  };

  const getFieldConfig = (fieldName) => {
    const activeFields = form?.activeFields || form?.active_fields || [];
    return activeFields.find(f => f.field_name === fieldName);
  };

  const renderField = (field) => {
    const fieldName = field.field_name;
    const value = formData[fieldName];
    const isRequired = field.is_required || false;
    const options = field.options || [];
    const baseType = field.field_type;

    switch (baseType) {
      case 'select':
        // For gender field, normalize the value to match options (case-insensitive)
        let selectValue = value || '';
        if (fieldName === 'gender' && selectValue && options.length > 0) {
          const valueLower = String(selectValue).toLowerCase();
          // Find matching option (case-insensitive)
          const matchingOption = options.find(opt => 
            String(opt).toLowerCase() === valueLower
          );
          if (matchingOption) {
            selectValue = matchingOption; // Use the exact option value
          }
        }
        
        return (
          <select
            value={selectValue}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            required={isRequired}
            style={{
              padding: '0.75rem',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.md,
              fontSize: theme.typography.fontSize.base,
              width: '100%',
            }}
          >
            <option value="">Select {field.field_label}</option>
            {options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            placeholder={field.placeholder}
            required={isRequired}
            style={{
              padding: '0.75rem',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.md,
              fontSize: theme.typography.fontSize.base,
              width: '100%',
              minHeight: '100px',
              resize: 'vertical',
            }}
          />
        );

      case 'radio':
        return (
          <RadioGroup>
            {options.map((option, idx) => (
              <RadioOption key={idx} theme={theme}>
                <input
                  type="radio"
                  name={fieldName}
                  value={option}
                  checked={value === option}
                  onChange={() => handleChange(fieldName, option)}
                  required={isRequired}
                />
                {option}
              </RadioOption>
            ))}
          </RadioGroup>
        );

      case 'checkbox':
        return (
          <CheckboxGrid>
            {options.map((option, idx) => {
              const selectedValues = Array.isArray(value) ? value : [];
              const selected = selectedValues.includes(option);
              return (
                <CheckboxOption key={idx} theme={theme} $selected={selected}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => {
                      const currentValues = Array.isArray(value) ? value : [];
                      const nextValues = selected
                        ? currentValues.filter(item => item !== option)
                        : [...currentValues, option];
                      handleChange(fieldName, nextValues);
                    }}
                  />
                  {option}
                </CheckboxOption>
              );
            })}
          </CheckboxGrid>
        );

      case 'file':
        return (
          <div>
            <input
              type="file"
              onChange={(e) => handleFileChange(fieldName, e.target.files?.[0] || null)}
              required={isRequired}
              style={{ display: 'none' }}
              ref={fieldName === 'cv' ? cvInputRef : null}
            />
            <FileUploadArea
              theme={theme}
              onClick={() => {
                const input = fieldName === 'cv' ? cvInputRef.current : document.querySelector(`input[type="file"][name="${fieldName}"]`);
                if (input) input.click();
              }}
            >
              <Upload size={24} />
              <p>Click to upload {field.field_label}</p>
            </FileUploadArea>
            {value instanceof File && (
              <FilePreview theme={theme}>
                <span>{value.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  onClick={() => handleFileChange(fieldName, null)}
                >
                  <X size={16} />
                </Button>
              </FilePreview>
            )}
          </div>
        );

      default:
        return (
          <Input
            type={baseType || 'text'}
            value={value || ''}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            placeholder={field.placeholder}
            required={isRequired}
          />
        );
    }
  };

  const renderDynamicField = (field) => (
    <FormField key={field.id || field.field_name}>
      <FieldLabel theme={theme}>
        {field.field_label}
        {field.is_required && <span className="required"> *</span>}
      </FieldLabel>
      {renderField(field)}
      {field.help_text && (
        <FieldHelp theme={theme}>{field.help_text}</FieldHelp>
      )}
      {fieldErrors[field.field_name] && (
        <FieldError theme={theme}>{fieldErrors[field.field_name]}</FieldError>
      )}
    </FormField>
  );

  const renderAccessibilityQuestion = () => (
    <FormField key="has_disability">
      <FieldLabel theme={theme}>Do you identify as having a physical disability?</FieldLabel>
      <RadioGroup>
        <RadioOption theme={theme}>
          <input
            type="radio"
            name="has_disability"
            value="no"
            checked={disabilityStatus !== 'yes'}
            onChange={() => handleDisabilityStatusChange('no')}
          />
          No
        </RadioOption>
        <RadioOption theme={theme}>
          <input
            type="radio"
            name="has_disability"
            value="yes"
            checked={disabilityStatus === 'yes'}
            onChange={() => handleDisabilityStatusChange('yes')}
          />
          Yes
        </RadioOption>
      </RadioGroup>
    </FormField>
  );

  const renderDisabilityDetails = () => {
    if (disabilityStatus !== 'yes') {
      return null;
    }

    return (
      <FormField key="disability_types">
        <FieldLabel theme={theme}>Select the disability type(s) that apply to you</FieldLabel>
        <CheckboxGrid>
          {DISABILITY_OPTIONS.map(option => {
            const selected = disabilitySelections.includes(option);
            return (
              <CheckboxOption key={option} theme={theme} $selected={selected}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleDisabilityType(option)}
                />
                <span>{option}</span>
              </CheckboxOption>
            );
          })}
        </CheckboxGrid>
        {disabilitySelections.includes(OTHER_DISABILITY_OPTION) && (
          <div style={{ marginTop: '1rem' }}>
            <FieldLabel theme={theme}>Disability details</FieldLabel>
            <Input
              placeholder="Please specify"
              value={disabilityOther}
              onChange={(e) => handleDisabilityOtherChange(e.target.value)}
            />
          </div>
        )}
      </FormField>
    );
  };

  const renderCvBlock = () => {
    // Prefer member.cv_url, but also fall back to latest_submission.files.cv if present
    const existingCvUrl =
      (member?.cv_url ? `${API_BASE_URL}/storage/${member.cv_url}` : null) ||
      member?.latest_submission?.files?.cv ||
      null;

    return (
      <FormField key="cv_upload">
        <FieldLabel theme={theme}>Upload CV / Resume (optional)</FieldLabel>
        <input
          type="file"
          ref={cvInputRef}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx"
          onChange={(e) => handleFileChange('cv', e.target.files?.[0] || null)}
        />
        <FileUploadArea
          theme={theme}
          onClick={() => {
            setRemoveCv(false);
            cvInputRef.current?.click();
          }}
        >
          <Upload size={24} />
          <p>Click to upload CV (PDF, DOC, DOCX)</p>
        </FileUploadArea>
        {formData.cv instanceof File && (
          <FilePreview theme={theme}>
            <span>{formData.cv.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={() => {
                handleFileChange('cv', null);
                setRemoveCv(false);
              }}
            >
              <X size={16} />
            </Button>
          </FilePreview>
        )}
        {isEditMode && existingCvUrl && !formData.cv && (
          <FilePreview theme={theme}>
            <span>Existing CV</span>
            <a href={existingCvUrl} target="_blank" rel="noopener noreferrer">
              View
            </a>
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={() => {
                // Mark CV for removal and clear any new file selection
                setRemoveCv(true);
                handleFileChange('cv', null);
                if (cvInputRef.current) {
                  cvInputRef.current.value = '';
                }
              }}
              style={{ color: theme.colors.error }}
            >
              Remove CV
            </Button>
          </FilePreview>
        )}
      </FormField>
    );
  };

  const renderIdCardBlock = () => {
    const fieldConfig = getFieldConfig('id_card');
    const label = fieldConfig?.field_label || 'ID Card';
    const isRequired = fieldConfig?.is_required || false;
    
    // Prefer member.id_document_url, but also fall back to latest_submission.files.id_card if present
    const existingIdUrl =
      (member?.id_document_url ? `${API_BASE_URL}/storage/${member.id_document_url}` : null) ||
      member?.latest_submission?.files?.id_card ||
      null;

    return (
      <FormField key="id_card">
        <FieldLabel theme={theme}>
          {label}
          {isRequired && <span className="required"> *</span>}
        </FieldLabel>
        <input
          type="file"
          ref={idCardRef}
          style={{ display: 'none' }}
          accept="image/*,.pdf"
          onChange={(e) => handleFileChange('id_card', e.target.files?.[0] || null)}
        />
        <FileUploadArea
          theme={theme}
          onClick={() => idCardRef.current?.click()}
        >
          <Upload size={24} />
          <p>Click to upload ID card (Image or PDF)</p>
        </FileUploadArea>
        {formData.id_card instanceof File && (
          <FilePreview theme={theme}>
            <span>{formData.id_card.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={() => handleFileChange('id_card', null)}
            >
              <X size={16} />
            </Button>
          </FilePreview>
        )}
        {isEditMode && existingIdUrl && !formData.id_card && (
          <FilePreview theme={theme}>
            <span>Existing ID document</span>
            <a href={existingIdUrl} target="_blank" rel="noopener noreferrer">
              View
            </a>
          </FilePreview>
        )}
        {fieldErrors.id_card && (
          <FieldError theme={theme}>{fieldErrors.id_card}</FieldError>
        )}
      </FormField>
    );
  };

  const renderInterestedTrainingsBlock = (fieldConfig) => {
    const options = fieldConfig?.options?.length > 0 ? fieldConfig.options : TRAINING_OPTIONS;
    const label = fieldConfig?.field_label || 'Interested Training(s)';
    const helpText = fieldConfig?.help_text || 'Select all training programs you\'re interested in joining';
    const isRequired = fieldConfig?.is_required || false;

    return (
      <FormField key="interested_trainings">
        <FieldLabel theme={theme}>
          {label}
          {isRequired && <span className="required"> *</span>}
        </FieldLabel>
        <FieldHelp theme={theme} style={{ marginBottom: '0.75rem' }}>
          {helpText}
        </FieldHelp>
        <CheckboxGrid>
          {options.map((option, idx) => {
            const selected = interestedTrainings.includes(option);
            return (
              <CheckboxOption key={idx} theme={theme} $selected={selected}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleInterestedTraining(option)}
                />
                {option}
              </CheckboxOption>
            );
          })}
        </CheckboxGrid>
      </FormField>
    );
  };

  const renderTrainingBlock = () => {
    const fieldConfig = getFieldConfig('has_previous_training');
    const label = fieldConfig?.field_label || 'Have you previously taken any training?';
    
    return (
      <>
        <FormField key="has_previous_training">
          <FieldLabel theme={theme}>{label}</FieldLabel>
          <RadioGroup>
            <RadioOption theme={theme}>
              <input
                type="radio"
                name="has_previous_training"
                value="no"
                checked={!hasPreviousTraining}
                onChange={() => {
                  setFormData(prev => ({
                    ...prev,
                    has_previous_training: 'no',
                    previous_trainings: [],
                  }));
                }}
              />
              No
            </RadioOption>
            <RadioOption theme={theme}>
              <input
                type="radio"
                name="has_previous_training"
                value="yes"
                checked={hasPreviousTraining}
                onChange={() => {
                  setFormData(prev => ({
                    ...prev,
                    has_previous_training: 'yes',
                    previous_trainings: prev.previous_trainings?.length ? prev.previous_trainings : [{ name: '', institution: '', date: '', certificate: null }]
                  }));
                }}
              />
              Yes
            </RadioOption>
          </RadioGroup>
        </FormField>

        {hasPreviousTraining && (
          <div style={{ 
            marginLeft: '1.5rem', 
            paddingLeft: '1.5rem', 
            borderLeft: `3px solid ${theme.colors.primary}40` 
          }}>
            {previousTrainings.map((training, index) => (
              <TrainingEntry key={`training_${index}`} theme={theme}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <FieldLabel theme={theme} style={{ margin: 0 }}>
                    Training #{index + 1}
                  </FieldLabel>
                  {previousTrainings.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => removePreviousTraining(index)}
                      style={{ color: theme.colors.error, padding: '0.25rem 0.5rem' }}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>

                <FieldLabel theme={theme}>
                  Training Name <span className="required">*</span>
                </FieldLabel>
                <Input
                  type="text"
                  value={training.name || ''}
                  onChange={(e) => updatePreviousTraining(index, 'name', e.target.value)}
                  placeholder="Name of the training program"
                />

                <div style={{ marginTop: '1rem' }}>
                  <FieldLabel theme={theme}>
                    Trainer / Institution Name <span className="required">*</span>
                  </FieldLabel>
                  <Input
                    type="text"
                    value={training.institution || ''}
                    onChange={(e) => updatePreviousTraining(index, 'institution', e.target.value)}
                    placeholder="Name of trainer or institution"
                  />
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <FieldLabel theme={theme}>Training Date</FieldLabel>
                  <Input
                    type="date"
                    value={training.date || ''}
                    onChange={(e) => updatePreviousTraining(index, 'date', e.target.value)}
                  />
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <FieldLabel theme={theme}>Certificate</FieldLabel>
                  <input
                    type="file"
                    ref={el => certificateRefs.current[index] = el}
                    style={{ display: 'none' }}
                    accept="image/*,.pdf"
                    onChange={(e) => updatePreviousTraining(index, 'certificate', e.target.files?.[0] || null)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="small"
                    onClick={() => certificateRefs.current[index]?.click()}
                    leftIcon={<Upload size={14} />}
                  >
                    {training.certificate ? training.certificate.name : 'Upload Certificate'}
                  </Button>
                  {/* Show existing certificate from latest submission if available */}
                  {member?.latest_submission?.files?.[`training_certificate_${index}`] && !training.certificate && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <a
                        href={member.latest_submission.files[`training_certificate_${index}`]}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View existing certificate
                      </a>
                    </div>
                  )}
                </div>
              </TrainingEntry>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addPreviousTraining}
              leftIcon={<Plus size={16} />}
            >
              Add Another Training
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderFormFields = () => {
    const activeFields = form?.activeFields || form?.active_fields || [];
    const elements = [];
    let accessibilityRendered = false;
    let cvRendered = false;
    let idCardRendered = false;
    let interestedTrainingsRendered = false;
    let previousTrainingRendered = false;

    activeFields.forEach(field => {
      const name = field.field_name;
      if (name === 'has_disability') {
        elements.push(renderAccessibilityQuestion());
        accessibilityRendered = true;
        const details = renderDisabilityDetails();
        if (details) {
          elements.push(details);
        }
      } else if (name === 'disability_types' || name === 'disability_other') {
        // handled within the accessibility block
      } else if (name === 'cv') {
        elements.push(renderCvBlock());
        cvRendered = true;
      } else if (name === 'id_card') {
        elements.push(renderIdCardBlock());
        idCardRendered = true;
      } else if (name === 'interested_trainings') {
        const fieldConfig = getFieldConfig('interested_trainings');
        elements.push(renderInterestedTrainingsBlock(fieldConfig));
        interestedTrainingsRendered = true;
      } else if (name === 'has_previous_training') {
        elements.push(renderTrainingBlock());
        previousTrainingRendered = true;
      } else {
        elements.push(renderDynamicField(field));
      }
    });

    // Add default fields if not in form config
    if (!accessibilityRendered) {
      elements.push(renderAccessibilityQuestion());
      const details = renderDisabilityDetails();
      if (details) {
        elements.push(details);
      }
    }
    if (!cvRendered) {
      elements.push(renderCvBlock());
    }
    if (!idCardRendered) {
      elements.push(renderIdCardBlock());
    }
    if (!interestedTrainingsRendered) {
      const fieldConfig = getFieldConfig('interested_trainings');
      elements.push(renderInterestedTrainingsBlock(fieldConfig));
    }
    if (!previousTrainingRendered) {
      elements.push(renderTrainingBlock());
    }

    return elements;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFieldErrors({});
    
    try {
      const payload = new FormData();

      // Normalize gender to match backend validation (female,male,other,prefer_not_to_say)
      let apiGender = '';
      if (formData.gender) {
        const g = String(formData.gender).toLowerCase().trim();
        if (['female', 'male', 'other', 'prefer_not_to_say'].includes(g)) {
          apiGender = g;
        } else if (g === 'f') {
          apiGender = 'female';
        } else if (g === 'm') {
          apiGender = 'male';
        } else {
          // fallback: keep original but lowercase
          apiGender = g;
        }
      }
      
      // Map form fields to member fields
      const fieldMapping = {
        first_name: formData.first_name || formData.full_name?.split(' ')[0] || '',
        middle_name: formData.middle_name || '',
        last_name: formData.last_name || formData.full_name?.split(' ').slice(-1)[0] || '',
        email: formData.email || '',
        phone: formData.phone || formData.phone_number || '',
        gender: apiGender,
        city: formData.region_city || '',
        organization_type: formData.sector || '',
        occupation: formData.occupation_position || '',
        organization_name: formData.organization_name || '',
        bio: formData.short_biography || '',
        membership_type: 'individual',
        membership_status: 'active',
        approval_status: 'approved',
      };
      
      // Add date of birth if age provided
      if (formData.age && !formData.date_of_birth) {
        const today = new Date();
        const birthYear = today.getFullYear() - parseInt(formData.age);
        fieldMapping.date_of_birth = `${birthYear}-01-01`;
      } else if (formData.date_of_birth) {
        fieldMapping.date_of_birth = formData.date_of_birth;
      }
      
      // Add thematic areas
      if (formData.thematic_areas) {
        const areas = typeof formData.thematic_areas === 'string'
          ? formData.thematic_areas.split(',').map(a => a.trim()).filter(Boolean)
          : formData.thematic_areas;
        payload.append('thematic_areas', JSON.stringify(areas));
      }
      
      // Add disability fields
      payload.append('has_disability', formData.has_disability === 'yes' ? '1' : '0');
      if (formData.has_disability === 'yes') {
        payload.append('disability_types', JSON.stringify(formData.disability_types || []));
        if (formData.disability_other) {
          payload.append('disability_other', formData.disability_other);
        }
      }
      
      // Add training fields
      if (formData.interested_trainings?.length > 0) {
        payload.append('interested_trainings', JSON.stringify(formData.interested_trainings));
      }
      
      payload.append('has_previous_training', formData.has_previous_training === 'yes' ? '1' : '0');
      
      if (formData.has_previous_training === 'yes' && formData.previous_trainings?.length > 0) {
        const trainingsData = formData.previous_trainings
          .filter(t => t.name)
          .map(t => ({
            training_name: t.name,
            institution: t.institution,
            training_date: t.date || ''
          }));
        payload.append('previous_trainings_data', JSON.stringify(trainingsData));
        
        // Add certificate files
        formData.previous_trainings.forEach((training, index) => {
          if (training.certificate instanceof File) {
            payload.append(`training_certificate_${index}`, training.certificate);
          }
        });
      }
      
      // Add basic fields
      Object.entries(fieldMapping).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          payload.append(key, value);
        }
      });
      
      // Add file uploads - align with backend expectations
      if (formData.id_card instanceof File) {
        // Backend expects 'id_document_file' for member update
        payload.append('id_document_file', formData.id_card);
      }
      if (formData.cv instanceof File) {
        // Backend expects 'cv_file' for member update
        payload.append('cv_file', formData.cv);
      }
      // CV removal flag
      if (isEditMode && removeCv && !formData.cv) {
        payload.append('remove_cv', '1');
      }
      
      if (isEditMode) {
        await membersAPI.update(id, payload);
        toast.success('Member updated successfully');
      } else {
        await membersAPI.create(payload);
        toast.success('Member created successfully');
      }
      
      navigate('/app/members');
    } catch (error) {
      console.error('Failed to save member', error);
      toast.error(error.response?.data?.message || 'Failed to save member');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container theme={theme}>
        <FormCard theme={theme}>
          <p>Loading...</p>
        </FormCard>
      </Container>
    );
  }

  return (
    <Container theme={theme}>
      <Header theme={theme}>
        <Button
          variant="ghost"
          onClick={() => navigate('/app/members')}
          leftIcon={<ArrowLeft size={16} />}
        >
          Back to Members
        </Button>
        <Title theme={theme}>
          {isEditMode ? 'Edit Member' : 'Add New Member'}
        </Title>
      </Header>

      <form onSubmit={handleSubmit}>
        <FormCard theme={theme}>
          {renderFormFields()}

          <ButtonGroup>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/app/members')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
              leftIcon={saving ? null : (isEditMode ? <Save size={16} /> : <UserPlus size={16} />)}
            >
              {saving ? 'Saving...' : (isEditMode ? 'Update Member' : 'Create Member')}
            </Button>
          </ButtonGroup>
        </FormCard>
      </form>
    </Container>
  );
};

export default AdminMemberForm;
